import { ActivityLocalStatus, getLocalStatusActivity, ListActivityAPIQueryParams } from '@goldcast/api/backend';
import { useMemo, useEffect, useState } from 'react';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';

import { updateClipAfterFetchingLatestAlignedRecordings } from '@/stores/clip';
import {
  getClipBroadcastId,
  isLiveEventRemotionWithPreviewAndAlignedStream
} from '@/player-config/src/utils/clipUtils';
import { ClipCompositionStatusType } from '@/components/molecules/SharePopup/ClipSharePopup/ClipCompositionStatus/types';
import { getSpeakerName } from '@/components/molecules/SharePopup/ClipSharePopup/ClipCompositionStatus/ClipCompositionStatusUtils';
import { getAlignedRecordings } from '@/player-config/src/utils/alignedRecordings';
import { useAppContext } from '@/context/AppContext/AppContext';
import { useContentMetadataStore } from '@/stores/contentMetaDataStore/contentMetaDataStore';
import {
  selectAlignedRecordings,
  selectSpeakerDetails
} from '@/player-config/src/selectors/contentMetadataStoreSelectors';

/**
 * @deprecated - This hook is only used for the old clip composition status.
 * To be removed with removal of Use_CL_Only_Local_Or_Cloud flag.
 */
export default function useClipCompositionStatus(): {
  showClipCompositionStatus: boolean;
  clipCompositionStatus: ClipCompositionStatusType;
  showClipCompositionLoader: boolean;
  affectedClipCompositionUsers: string[];
} {
  const { clipData } = useClipsContext();
  const { logger } = useAppContext();
  const [affectedClipCompositionUsers, setAffectedClipCompositionUsers] = useState<string[]>([]);
  const [isUpdatingClip, setIsUpdatingClip] = useState(false);
  const [localStatusActivity, setLocalStatusActivity] = useState<ActivityLocalStatus | null>(null);
  const [isFetchingLocalStatusActivity, setIsFetchingLocalStatusActivity] = useState(false);
  const broadcastId = getClipBroadcastId(clipData);
  const alignedRecordingsData = useContentMetadataStore(selectAlignedRecordings(broadcastId ?? ''));

  const clipBroadcastId = useMemo(() => getClipBroadcastId(clipData), [clipData]);
  const recordings = useMemo(
    () =>
      getAlignedRecordings({
        broadcastId: clipBroadcastId,
        snapshot: alignedRecordingsData,
        contentMetadataStoreInstance: useContentMetadataStore
      }),
    [clipBroadcastId, alignedRecordingsData]
  );

  const showClipCompositionStatus = useMemo(() => {
    return false;
  }, []);

  useEffect(() => {
    if (!showClipCompositionStatus) {
      return;
    }

    const fetchLocalStatusActivity = async () => {
      setIsFetchingLocalStatusActivity(true);
      try {
        const response = await getLocalStatusActivity({
          id: clipData.content.id,
          queryParams: {}
        });
        setLocalStatusActivity(response);
      } catch (error) {
        logger.error('Error fetching local status activity:', error);
      } finally {
        setIsFetchingLocalStatusActivity(false);
      }
    };

    fetchLocalStatusActivity();
  }, [showClipCompositionStatus, clipData.content.id]);

  const speakerDetails = useContentMetadataStore(selectSpeakerDetails(clipBroadcastId ?? ''));

  useEffect(() => {
    if (!localStatusActivity || localStatusActivity.users.length === 0) {
      return;
    }

    const users = localStatusActivity.users ?? [];

    const _affectedClipCompositionusers = recordings
      .filter(recording => users.includes(recording.user.id))
      .map(recording => getSpeakerName(recording, speakerDetails));

    setAffectedClipCompositionUsers(_affectedClipCompositionusers);
  }, [localStatusActivity?.users, speakerDetails]);

  const status = localStatusActivity?.status;
  useEffect(() => {
    if (status === 'done') {
      setIsUpdatingClip(true);
      const queryParams: ListActivityAPIQueryParams = isLiveEventRemotionWithPreviewAndAlignedStream(clipData)
        ? { status: 'complete' }
        : {};
      updateClipAfterFetchingLatestAlignedRecordings(clipData.content.id, clipData.id, queryParams).finally(() => {
        setIsUpdatingClip(false);
      });
    }
  }, [status]);

  return {
    showClipCompositionStatus,
    showClipCompositionLoader: isFetchingLocalStatusActivity || isUpdatingClip,
    clipCompositionStatus: localStatusActivity?.status || 'done',
    affectedClipCompositionUsers
  };
}
